import { jsxs, jsx } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { styled } from '@flex-design-system/stitches-react';
import { getYear, format, isAfter } from 'date-fns';
import { useState, useEffect } from 'react';
import { Button } from '../../../button/button/Button.mjs';
import getLocaleData from '../../../locale/getLocaleData.mjs';
import { useLinearLocale } from '../../../locale/LocaleContext.mjs';
import { SELECTED_DAY_BACKGROUND_COLOR_CSS_VARIABLE } from '../../constants/styles.mjs';
import { useDatePickerContext } from '../../context/datePickerContext.mjs';
import { isSameYearMonth, isBeforeYearMonth, isAfterYearMonth, isAfterOrSameYearMonth, isBeforeOrSameYearMonth } from '../../utils/date.mjs';
import Header from './Header.mjs';

const DATE_PICKER_MONTH_CALENDAR_NAME = "DatePickerMonthCalendar";
function MonthCalendar({
  __scopeDatePicker,
  onYearChange
}) {
  const [hoverMonth, setHoverMonth] = useState(void 0);
  const {
    mode,
    minDate,
    maxDate,
    startDate,
    endDate,
    selectedDate,
    onDateChange,
    onStartDateChange,
    onEndDateChange,
    excludeDates = [],
    openToDate
  } = useDatePickerContext(DATE_PICKER_MONTH_CALENDAR_NAME, __scopeDatePicker);
  const { locale } = useLinearLocale();
  const dateFnsLocaleData = getLocaleData(locale);
  const isRangeMode = mode === "range";
  const [year, setYear] = useState(() => {
    const nowYear = getYear(Date.now());
    if (isRangeMode) {
      return startDate !== null ? getYear(startDate) : nowYear;
    } else {
      return selectedDate !== null ? getYear(selectedDate) : nowYear;
    }
  });
  useEffect(() => {
    if (selectedDate !== null) {
      setYear(getYear(selectedDate));
    }
    if (isRangeMode && endDate !== null) {
      setYear(getYear(endDate));
    }
  }, [selectedDate, endDate, isRangeMode]);
  const getIsSelected = (monthIndex) => {
    const compareDate = new Date(year, monthIndex);
    if (isRangeMode) {
      return getIsRangeStartDate(monthIndex) || getIsRangeEndDate(monthIndex);
    } else {
      return selectedDate !== null && isSameYearMonth(selectedDate, compareDate);
    }
  };
  const getIsRangeStartDate = (monthIndex) => {
    const compareDate = new Date(year, monthIndex);
    if (isRangeMode && startDate !== null) {
      return isSameYearMonth(startDate, compareDate);
    }
    return false;
  };
  const getIsRangeEndDate = (monthIndex) => {
    const compareDate = new Date(year, monthIndex);
    if (isRangeMode && endDate !== null) {
      return isSameYearMonth(endDate, compareDate);
    }
    return false;
  };
  const getIsInRange = (monthIndex) => {
    if (!isRangeMode) {
      return false;
    }
    const compareDate = new Date(year, monthIndex);
    if (startDate !== null && endDate !== null) {
      return isAfterOrSameYearMonth(compareDate, startDate) && isBeforeOrSameYearMonth(compareDate, endDate);
    }
    if (startDate !== null && endDate === null && hoverMonth !== void 0) {
      const hoveredMonthDate = new Date(year, hoverMonth);
      return isAfterYearMonth(compareDate, startDate) && isBeforeOrSameYearMonth(compareDate, hoveredMonthDate);
    }
  };
  const handleChangeMonthIndex = (monthIndex) => {
    const compareDate = new Date(year, monthIndex);
    if (mode === "single") {
      onDateChange(compareDate);
    } else if (isRangeMode) {
      if (startDate !== null && endDate !== null) {
        onStartDateChange(compareDate);
        onEndDateChange(null);
        return;
      }
      if (startDate === null || isBeforeYearMonth(compareDate, startDate)) {
        onStartDateChange(compareDate);
      } else if (isSameYearMonth(compareDate, startDate) || isAfter(compareDate, startDate)) {
        onEndDateChange(compareDate);
      }
    }
  };
  const handleIncreaseYear = () => {
    const newYear = year + 1;
    setYear(newYear);
    onYearChange?.(newYear);
  };
  const handleDecreaseYear = () => {
    const newYear = year - 1;
    setYear(newYear);
    onYearChange?.(newYear);
  };
  const getIsDisabled = (monthIndex) => {
    const compareDate = new Date(year, monthIndex);
    const isExcludedYearMonth = excludeDates.find(
      (excludeDate) => isSameYearMonth(compareDate, excludeDate)
    ) !== void 0;
    const isUnderMinYearMonth = !isNil(minDate) && isBeforeYearMonth(compareDate, minDate);
    const isOverMaxYearMonth = !isNil(maxDate) && isAfterYearMonth(compareDate, maxDate);
    return isExcludedYearMonth || isUnderMinYearMonth || isOverMaxYearMonth;
  };
  const handleMouseEnter = (monthIndex) => {
    setHoverMonth(monthIndex);
  };
  const handleMouseLeave = () => {
    setHoverMonth(void 0);
  };
  return /* @__PURE__ */ jsxs("div", { style: { padding: "10px" }, children: [
    /* @__PURE__ */ jsx(
      Header,
      {
        year: openToDate ? getYear(openToDate) : year,
        onClickNextYear: handleIncreaseYear,
        onClickPrevYear: handleDecreaseYear
      }
    ),
    /* @__PURE__ */ jsx(Wrapper, { children: Array.from({ length: 12 }).map((_, monthIndex) => {
      const disabled = getIsDisabled(monthIndex);
      const buttonProps = {
        key: monthIndex,
        children: format(new Date(year, monthIndex), "MMM", {
          locale: dateFnsLocaleData
        })
      };
      const isSelected = getIsSelected(monthIndex);
      return disabled ? /* @__PURE__ */ jsx(
        StyledButton,
        {
          ...buttonProps,
          disabled: true,
          variant: "ghost",
          color: "grayDarker",
          "data-selected": isSelected
        }
      ) : /* @__PURE__ */ jsx(
        StyledButton,
        {
          ...buttonProps,
          variant: isSelected ? "solid" : "ghost",
          color: isSelected ? "green" : "grayDarker",
          "data-selected": isSelected,
          isRangeStartDate: getIsRangeStartDate(monthIndex),
          isRangeEndDate: getIsRangeEndDate(monthIndex),
          isInRange: getIsInRange(monthIndex),
          onClick: () => handleChangeMonthIndex(monthIndex),
          onPointerEnter: () => handleMouseEnter(monthIndex),
          onPointerLeave: handleMouseLeave
        }
      );
    }) })
  ] });
}
const Wrapper = styled("div", {
  [SELECTED_DAY_BACKGROUND_COLOR_CSS_VARIABLE]: "#E5FAE8",
  display: "grid",
  gridTemplateRows: "repeat(4, 40px)",
  gridTemplateColumns: "repeat(3, 1fr)"
});
const StyledButton = styled(Button, {
  ['&[data-selected="true"]']: {
    fontWeight: 600,
    ['&[aria-disabled="true"]']: {
      fontWeight: 500
    }
  },
  ['&[data-selected="false"]']: {
    fontWeight: 500
  },
  variants: {
    isRangeStartDate: {
      true: {
        borderTopRightRadius: "0px",
        borderBottomRightRadius: "0px"
      }
    },
    isRangeEndDate: {
      true: {
        borderTopLeftRadius: "0px",
        borderBottomLeftRadius: "0px"
      }
    },
    isInRange: {
      true: {}
    }
  },
  compoundVariants: [
    {
      isRangeStartDate: true,
      isRangeEndDate: true,
      css: { borderRadius: "8px" }
    },
    {
      isInRange: true,
      isRangeStartDate: false,
      isRangeEndDate: false,
      css: {
        backgroundColor: `var(${SELECTED_DAY_BACKGROUND_COLOR_CSS_VARIABLE}) !important`,
        borderRadius: "0px"
      }
    }
  ]
});

export { MonthCalendar as default };
//# sourceMappingURL=index.mjs.map
